import { urlize } from 'mk/bazaar/common/filters';
import { reactFilter } from 'mk/bazaar/common/filters/utils';
import { AST } from 'mk2/apps/wiki/schemas';
import {
    constructGiphy,
    constructImage,
    constructInstagram,
    constructVimeo,
    constructYoutube,
    linkProcessor,
} from 'mk2/apps/wiki/utils';
import { PhotoEntity } from 'mk2/schemas';
import * as React from 'react';

type ReactChild = React.ReactChild;

export interface FilterAdditionalData {
    countExperiences: {[articleUrl: string]: number};
    countTopicContributors: {[topicSlug: string]: number};
    categorySlugs: {[articleSlug: string]: string};
    linkedObjects: {[objType: string]: {[objId: number]: {url: string}}};
    photos: PhotoEntity[];
    photoStyle: any;
}

const removeQuotes = (s: string): string => s.replace(/^"|"$/g, '');

export const processNodeOfAST = (ast: AST, additionalData: FilterAdditionalData): ReactChild[] => {
    let ret: ReactChild[] = [];

    if (ast.type === 'document') {
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<div>{ret}</div>)];
        ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);

    } else if (ast.type === 'paragraph') {
        ret = [];

        let p: ReactChild[] = [];
        let childOfP = true;

        if (ast.elements) {
            for (const e of ast.elements) {
                if (e.type === 'paragraph_line') {
                    if (e.elements) {
                        for (const ee of e.elements) {
                            childOfP = !(
                                ee.type === 'table' ||
                                ee.type === 'numbered_list' ||
                                ee.type === 'unnumbered_list' ||
                                ee.type === 'hr' ||
                                ee.type === 'header1' ||
                                ee.type === 'header2' ||
                                ee.type === 'header3'
                            );

                            if (childOfP) {
                                // if element can be a child of <p>, collect them to the array p and finally append <p>{p}</p> to array ret
                                p.push(...processNodeOfAST(ee, additionalData));

                            } else {
                                // if element can't be a child of <p>, then append past elements as <p>{p}</p> to array ret and then append the actual non p element to array ret
                                if (p.length > 0) {
                                    p = reactFilter(p, (s: string): React.ReactChild[] => [s]);
                                    ret.push((<p>{p}</p>));
                                    p = [];
                                }
                                ret.push(...processNodeOfAST(ee, additionalData));
                            }
                        }

                        // append <br> after each paragraph_line, which ends with element "childable" to <p>
                        if (childOfP) {
                            p.push((<br />));
                        }
                    }
                } else {
                    // append non paragraph_line element (header / paragraph_endblanks) separately
                    ret.push(...processNodeOfAST(e, additionalData));
                }
            }
            if (p.length > 0) {
                p = reactFilter(p, (s: string): React.ReactChild[] => [s]);
                ret.push((<p>{p}</p>));
            }
        }

    } else if (ast.type === 'paragraph_endblanks') {
        ret = [];

    } else if (ast.type === 'numbered_list') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<ol>{ret}</ol>)];

    } else if (ast.type === 'numbered_list_item_lev1') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<li>{ret}</li>)];

    } else if (ast.type === 'unnumbered_list') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<ul>{ret}</ul>)];

    } else if (ast.type === 'unnumbered_list_item_lev1') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<li>{ret}</li>)];

    } else if (ast.type === 'unnumbered_list_item_lev2') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<ul><li>{ret}</li></ul>)];

    } else if (ast.type === 'table') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<table>{ret}</table>)];

    } else if (ast.type === 'table_row') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<tbody><tr>{ret}</tr></tbody>)];

    } else if (ast.type === 'table_header_cell') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<th>{ret}</th>)];

    } else if (ast.type === 'table_cell') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<td>{ret}</td>)];

    } else if (ast.type === 'header1') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<h2>{ret}</h2>)];

    } else if (ast.type === 'header2') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<h3>{ret}</h3>)];

    } else if (ast.type === 'header3') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<h4>{ret}</h4>)];

    } else if (ast.type === 'bold') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<b>{ret}</b>)];

    } else if (ast.type === 'italic') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<i>{ret}</i>)];

    } else if (ast.type === 'code') {
        ret = [];
        if (ast.elements) {
            for (const e of ast.elements) {
                ret.push(...processNodeOfAST(e, additionalData));
            }
            ret = reactFilter(ret, (s: string): React.ReactChild[] => [s]);
        }
        ret = [(<code>{ret}</code>)];

    } else if (ast.type === 'link') {
        const linkAddressNode = ast.elements.find((e) => e.type === 'link_address');
        let linkAddressText = linkAddressNode ? linkAddressNode.text : '';

        const linkToObjectNode = ast.elements.find((e) => e.type === 'link_to_object');
        const objType = linkToObjectNode ? linkToObjectNode.obj_type : '';
        const objId = linkToObjectNode ? linkToObjectNode.obj_id : null;
        if (objType && objId && additionalData.linkedObjects) {
            const objData = additionalData.linkedObjects[objType][objId];
            linkAddressText = objData.url;
        }

        const plainTextNode = ast.elements.find((e) => e.type === 'plain_text');
        const plainTextText = plainTextNode ? plainTextNode.text : '';

        if (linkAddressText !== '' && plainTextText !== '') {
            // urlize links entered with markdown
            ret = [linkProcessor(linkAddressText, plainTextText, additionalData.countExperiences, additionalData.countTopicContributors, additionalData.categorySlugs)];
        } else if (linkAddressText !== '') {
            // urlize links entered without markdown, but as plaintext
            ret = urlize(linkAddressText, 50);
        }

    } else if (ast.type === 'pic') {
        let cd = '';
        let align = '';
        let size = '';
        let title = '';
        for (const element of ast.elements) {
            const attributeName = element.elements.find((e) => e.type === 'attribute_name').text;
            const attributeValue = element.elements.find((e) => e.type === 'attribute_value').text;
            cd = attributeName === 'cd' ? removeQuotes(attributeValue) : cd;
            align = attributeName === 'align' ? removeQuotes(attributeValue) : align;
            size = attributeName === 'size' ? removeQuotes(attributeValue) : size;
            title = attributeName === 'title' ? removeQuotes(attributeValue) : title;
        }
        const imgElement = constructImage(
            additionalData.photos,
            additionalData.photoStyle,
            cd,
            align,
            size,
            title,
        );
        ret = Array.isArray(imgElement) ? imgElement : [imgElement];

    } else if (['youtube', 'vimeo', 'giphy', 'instagram'].includes(ast.type)) {
        ret = [];

        let code = '';
        let width = '';
        let height = '';

        for (const element of ast.elements) {
            const attributeName = element.elements.find((e) => e.type === 'attribute_name').text;
            const attributeValue = element.elements.find((e) => e.type === 'attribute_value').text;
            code = attributeName === 'code' ? removeQuotes(attributeValue) : code;
            width = attributeName === 'width' ? removeQuotes(attributeValue) : width;
            height = attributeName === 'height' ? removeQuotes(attributeValue) : height;
        }

        if (ast.type === 'youtube') {
            const youtubeElement = constructYoutube(
                code,
                width ? parseInt(width, 10) : 560,
                height ? parseInt(height, 10) : 315,
            );
            ret = [youtubeElement];
        }
        if (ast.type === 'vimeo') {
            const vimeoElement = constructVimeo(
                code,
                width ? parseInt(width, 10) : 560,
                height ? parseInt(height, 10) : 315,
            );
            ret = [vimeoElement];
        }
        if (ast.type === 'instagram') {
            const instagramElement = constructInstagram(
                code,
                height ? parseInt(height, 10) : 1040,
            );
            ret = [instagramElement];
        }
        if (ast.type === 'giphy') {
            const giphyElement = constructGiphy(
                code,
                width ? parseInt(width, 10) : 560,
                height ? parseInt(height, 10) : 315,
            );
            ret = [giphyElement];
        }

    } else if (ast.type === 'plain_text') {
        ret = [];
        if (ast.text) {
            ret = [ast.text];
        }

    } else if (ast.type === 'emoji') {
        ret = [];
        if (ast.text) {
            ret = [ast.text];
        }

    } else if (ast.type === 'hr') {
        ret.push(<hr />);

    } else {
        throw new Error(`unknown node type in the ast - ${ast.type}`);
    }

    return ret;
};
